// client/src/pages/UserRegister/index.jsx - עם אימות מייל
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { requestEmailVerification, getSectors } from "../../api/userApi";
import {
  PageContainer,
  FormCard,
  FormHeader,
  IconWrapper,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  Select,
  FieldError,
  FieldSuccess,
  FieldHint,
  Alert,
  AlertIcon,
  SubmitButton,
  Spinner,
  LinksContainer,
  StyledLink,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  PasswordStrength,
  StrengthBar,
  StrengthText
} from "./styles";

// פונקציה לבדיקת תקינות תעודת זהות ישראלית
const isValidIsraeliID = (id = "") => {
  id = String(id).trim().padStart(9, "0");
  if (!/^\d{9}$/.test(id)) return false;

  const checksum = id.split("").reduce((sum, digit, index) => {
    let value = +digit * ((index % 2) + 1);
    return sum + (value > 9 ? value - 9 : value);
  }, 0);

  return checksum % 10 === 0;
};

// פונקציה לבדיקת חוזק סיסמה
const checkPasswordStrength = (password) => {
  if (!password) return null;
  if (password.length < 6) return 'weak';
  if (password.length < 8 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) return 'medium';
  return 'strong';
};

const getStrengthText = (strength) => {
  switch(strength) {
    case 'weak': return 'סיסמה חלשה';
    case 'medium': return 'סיסמה בינונית';
    case 'strong': return 'סיסמה חזקה';
    default: return '';
  }
};

const UserRegister = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    username: "",
    email: "",
    nationalId: "",
    sector: "",
    password: "",
    confirmPassword: "",
  });
  const [sectors, setSectors] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [idError, setIdError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(null);

  const navigate = useNavigate();

  // טעינת רשימת הסקטורים
  useEffect(() => {
    const loadSectors = async () => {
      try {
        const response = await getSectors();
        if (response.success) {
          setSectors(response.sectors);
        } else {
          setError("שגיאה בטעינת רשימת הסקטורים");
        }
      } catch (error) {
        console.error("Error loading sectors:", error);
        setError("שגיאה בטעינת רשימת הסקטורים");
      } finally {
        setSectorsLoading(false);
      }
    };

    loadSectors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // בדיקת חוזק סיסמה
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }

    // ניקוי שגיאות כשהמשתמש מתחיל להקליד
    if (error) setError("");
    if (success) setSuccess("");
    if (name === "nationalId" && idError) setIdError("");
  };

  const handleIdBlur = () => {
    if (formData.nationalId && !isValidIsraeliID(formData.nationalId)) {
      setIdError("תעודת זהות לא תקינה");
    } else {
      setIdError("");
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError("הסיסמאות אינן תואמות");
      return false;
    }
    if (formData.password.length < 6) {
      setError("הסיסמה חייבת להכיל לפחות 6 תווים");
      return false;
    }
    if (!formData.nationalId.trim()) {
      setError("תעודת זהות נדרשת");
      return false;
    }
    if (!isValidIsraeliID(formData.nationalId)) {
      setIdError("תעודת זהות לא תקינה");
      setError("תעודת זהות לא תקינה");
      return false;
    }
    if (!formData.sector) {
      setError("יש לבחור סקטור");
      return false;
    }
    if (!formData.email.includes("@") || !formData.email.includes(".")) {
      setError("כתובת אימייל לא תקינה");
      return false;
    }
    if (formData.username.length < 3) {
      setError("שם משתמש חייב להכיל לפחות 3 תווים");
      return false;
    }
    if (formData.fullName.length < 2) {
      setError("יש להזין שם מלא");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setIdError("");

    try {
      const { confirmPassword, ...dataToSend } = formData;
      dataToSend.nationalId = dataToSend.nationalId.trim().padStart(9, "0");

      const response = await requestEmailVerification(dataToSend);

      if (response.success) {
        setSuccess("קוד אימות נשלח לכתובת המייל שלך! עבור לדף האימות כדי להמשיך.");

        localStorage.setItem("pendingVerificationEmail", dataToSend.email);
        localStorage.setItem("pendingVerificationData", JSON.stringify(dataToSend));

        setTimeout(() => {
          navigate("/user/verify-email", {
            state: {
              email: dataToSend.email,
              fromRegistration: true,
            },
          });
        }, 2000);
      } else {
        setError(response.message || "שגיאה בשליחת קוד האימות");
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);

      if (error.response?.status === 429) {
        setError("יותר מדי בקשות. נסה שוב מאוחר יותר");
      } else if (error.response?.status === 400) {
        setError(error.response.data?.message || "שגיאה בנתונים שהוזנו");
      } else if (error.response?.status === 409) {
        setError("משתמש כבר קיים במערכת");
      } else if (error.response?.status === 500) {
        setError("שגיאה בשרת. נסה שוב מאוחר יותר");
      } else {
        setError("שגיאה בשליחת בקשת הרישום. נסה שוב");
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    !loading &&
    !idError &&
    formData.nationalId &&
    isValidIsraeliID(formData.nationalId) &&
    formData.sector &&
    formData.username.length >= 3 &&
    formData.fullName.length >= 2 &&
    formData.email.includes("@") &&
    formData.password.length >= 6 &&
    formData.password === formData.confirmPassword;

  // מצב טעינה ראשוני
  if (sectorsLoading) {
    return (
      <PageContainer>
        <FormCard>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>טוען נתוני מערכת...</LoadingText>
          </LoadingContainer>
        </FormCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormCard>
        <FormHeader>
          <IconWrapper>📧</IconWrapper>
          <Title>הרשמה למערכת</Title>
          <Subtitle>צור חשבון חדש ואמת את האימייל שלך</Subtitle>
        </FormHeader>

        {error && (
          <Alert type="error">
            <AlertIcon>⚠️</AlertIcon>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success">
            <AlertIcon>✅</AlertIcon>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>שם מלא <span>*</span></Label>
            <Input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="הכנס שם מלא"
              required
              disabled={loading}
            />
          </FormGroup>

          <FormGroup>
            <Label>שם משתמש <span>*</span></Label>
            <Input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="בחר שם משתמש (לפחות 3 תווים)"
              required
              disabled={loading}
            />
            {formData.username.length > 0 && formData.username.length < 3 && (
              <FieldError>שם משתמש חייב להכיל לפחות 3 תווים</FieldError>
            )}
          </FormGroup>

          <FormGroup>
            <Label>אימייל <span>*</span></Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="הכנס כתובת אימייל תקפה"
              required
              disabled={loading}
            />
            <FieldHint>📧 קוד אימות יישלח לכתובת זו</FieldHint>
          </FormGroup>

          <FormGroup>
            <Label>תעודת זהות <span>*</span></Label>
            <Input
              type="text"
              name="nationalId"
              value={formData.nationalId}
              onChange={handleChange}
              onBlur={handleIdBlur}
              placeholder="הכנס תעודת זהות (9 ספרות)"
              required
              disabled={loading}
              hasError={!!idError}
              maxLength="9"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
              }}
            />
            {idError && <FieldError>{idError}</FieldError>}
            {formData.nationalId && isValidIsraeliID(formData.nationalId) && (
              <FieldSuccess>✅ תעודת זהות תקינה</FieldSuccess>
            )}
          </FormGroup>

          <FormGroup>
            <Label>סקטור <span>*</span></Label>
            <Select
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="">בחר סקטור...</option>
              {sectors.map((sector, index) => (
                <option key={index} value={sector}>
                  {sector}
                </option>
              ))}
            </Select>
            {formData.sector && (
              <FieldSuccess>✅ נבחר: {formData.sector}</FieldSuccess>
            )}
          </FormGroup>

          <FormGroup>
            <Label>סיסמה <span>*</span></Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="בחר סיסמה (לפחות 6 תווים)"
              required
              disabled={loading}
            />
            {formData.password.length > 0 && formData.password.length < 6 && (
              <FieldError>הסיסמה חייבת להכיל לפחות 6 תווים</FieldError>
            )}
            {passwordStrength && formData.password.length >= 6 && (
              <>
                <PasswordStrength>
                  <StrengthBar active={true} strength={passwordStrength} />
                  <StrengthBar active={passwordStrength !== 'weak'} strength={passwordStrength} />
                  <StrengthBar active={passwordStrength === 'strong'} strength={passwordStrength} />
                </PasswordStrength>
                <StrengthText strength={passwordStrength}>
                  {getStrengthText(passwordStrength)}
                </StrengthText>
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>אימות סיסמה <span>*</span></Label>
            <Input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="הכנס שוב את הסיסמה"
              required
              disabled={loading}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <FieldError>הסיסמאות אינן תואמות</FieldError>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password.length >= 6 && (
              <FieldSuccess>✅ הסיסמאות תואמות</FieldSuccess>
            )}
          </FormGroup>

          <SubmitButton type="submit" disabled={!isFormValid}>
            {loading ? (
              <>
                <Spinner />
                שולח קוד אימות...
              </>
            ) : (
              '📧 שלח קוד אימות'
            )}
          </SubmitButton>
        </Form>

        <LinksContainer>
          <StyledLink 
            onClick={() => !loading && navigate("/user/login")}
            disabled={loading}
          >
            כבר יש לך חשבון? התחבר כאן
          </StyledLink>
          
          <StyledLink 
            highlight
            onClick={() => !loading && navigate("/user/verify-email")}
            disabled={loading}
          >
            כבר יש לך קוד אימות? לחץ כאן
          </StyledLink>
        </LinksContainer>
      </FormCard>
    </PageContainer>
  );
};

export default UserRegister;